
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Database } from '@/integrations/supabase/types';
import { Tag } from 'lucide-react';

// Using a more flexible type definition
type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  tags?: { id: string; name: string }[];
};

interface RelatedPostsSidebarProps {
  relatedPosts: BlogPost[];
  popularPosts: BlogPost[];
  tags?: { id: string; name: string }[];
  onPostClick?: () => void;
}

export function RelatedPostsSidebar({ 
  relatedPosts, 
  popularPosts, 
  tags, 
  onPostClick 
}: RelatedPostsSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handlePostClick = (slug: string | undefined) => {
    if (!slug) return;
    
    if (onPostClick) {
      onPostClick();
    }
    
    // Use proper React Router navigation
    navigate(`/blog/${slug}`);
  };

  const handleTagClick = (tagName: string) => {
    // Use proper React Router navigation for tags
    navigate(`/blog?tag=${tagName}`);
  };

  return (
    <div className="space-y-6">
      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-medium text-lg mb-4">Related Posts</h3>
            <div className="space-y-4">
              {relatedPosts.map(post => (
                <div key={post.id} className="group">
                  <button 
                    className="text-left block w-full"
                    onClick={() => handlePostClick(post.slug)}
                  >
                    <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h4>
                    {post.created_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </p>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Popular Posts */}
      {popularPosts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-medium text-lg mb-4">Popular Posts</h3>
            <div className="space-y-4">
              {popularPosts.map(post => (
                <div key={post.id} className="group">
                  <button 
                    className="text-left block w-full"
                    onClick={() => handlePostClick(post.slug)}
                  >
                    <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h4>
                    {post.view_count !== undefined && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {post.view_count} {post.view_count === 1 ? 'view' : 'views'}
                      </p>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tags */}
      {tags && tags.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium text-lg">Tags</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <button 
                  key={tag.id}
                  onClick={() => handleTagClick(tag.name)}
                  className="inline-block"
                >
                  <Badge variant="secondary" className="hover:bg-secondary">
                    {tag.name}
                  </Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
